import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import api, { onFeatureLocked } from '@/lib/api'

export type Feature =
  | 'ai_chat_agent' | 'knowledge_base' | 'lead_scoring' | 'booking_calendar'
  | 'chat_transcripts' | 'human_takeover_chat'
  | 'ai_inbound_agent' | 'phone_number' | 'call_transcripts' | 'call_summaries' | 'caller_history'
  | 'ai_outbound_agent' | 'auto_callback' | 'auto_follow_up' | 'reminder_calls'
  | 'outreach_lists' | 'crm_integrations' | 'priority_support'

export type Tier = 'associate' | 'partner' | 'principal'

export interface DemoInfo {
  status: 'active' | 'expired' | 'archived'
  expiresAt: string | null
  daysLeft: number
}

export interface AffiliateSandboxInfo {
  enabled: boolean
  notice: string
}

interface FeatureGateState {
  tier: string | null
  isPlatformAdmin: boolean
  isSuperAdmin: boolean
  isComped: boolean
  active: Set<Feature>
  locked: Map<Feature, Tier>
  demo: DemoInfo | null
  affiliateSandbox: AffiliateSandboxInfo | null
  loading: boolean
}

interface FeatureGateContextValue extends FeatureGateState {
  isAllowed: (feature: Feature) => boolean
  requiredTier: (feature: Feature) => Tier | null
  refresh: () => Promise<void>
  /** Open the upgrade modal for a specific feature (used by the 402 interceptor). */
  requestUpgrade: (feature: Feature, requiredTier: Tier) => void
  /** Current upgrade prompt target, or null if closed. */
  upgradePrompt: { feature: Feature; requiredTier: Tier } | null
  closeUpgrade: () => void
}

const FeatureGateContext = createContext<FeatureGateContextValue>({
  tier: null,
  isPlatformAdmin: false,
  isSuperAdmin: false,
  isComped: false,
  active: new Set(),
  locked: new Map(),
  demo: null,
  affiliateSandbox: null,
  loading: true,
  isAllowed: () => true,
  requiredTier: () => null,
  refresh: async () => {},
  requestUpgrade: () => {},
  upgradePrompt: null,
  closeUpgrade: () => {},
})

export function FeatureGateProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<FeatureGateState>({
    tier: null,
    isPlatformAdmin: false,
    isSuperAdmin: false,
    isComped: false,
    active: new Set(),
    locked: new Map(),
    demo: null,
    affiliateSandbox: null,
    loading: true,
  })
  const [upgradePrompt, setUpgradePrompt] = useState<FeatureGateContextValue['upgradePrompt']>(null)

  const refresh = useCallback(async () => {
    try {
      const token = localStorage.getItem('app_auth_token')
      if (!token) {
        setState((s) => ({ ...s, loading: false }))
        return
      }
      const res = await api.get('/api/billing/features')
      if (res.data.success) {
        const d = res.data.data
        setState({
          tier: d.tier,
          isPlatformAdmin: Boolean(d.isPlatformAdmin),
          isSuperAdmin: Boolean(d.isSuperAdmin),
          isComped: Boolean(d.isComped),
          active: new Set(d.active as Feature[]),
          locked: new Map((d.locked as Array<{ feature: Feature; requiredTier: Tier }>).map(
            (l) => [l.feature, l.requiredTier]
          )),
          demo: d.demo ?? null,
          affiliateSandbox: d.affiliateSandbox ?? null,
          loading: false,
        })
      } else {
        setState((s) => ({ ...s, loading: false }))
      }
    } catch {
      // Fail open — if we can't reach the endpoint, let users through.
      // Backend middleware is the real enforcement; this is UX.
      setState((s) => ({ ...s, loading: false }))
    }
  }, [])

  useEffect(() => {
    refresh()

    // Listen for auth changes across tabs so the feature map follows
    // logins/logouts. (Same-tab localStorage.setItem does NOT fire a
    // storage event — cross-tab only.)
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'app_auth_token' || e.key === 'app_agent_data') {
        refresh()
      }
    }
    window.addEventListener('storage', onStorage)

    // Refresh when the user comes back to this tab — catches tier changes
    // made in another tab (super admin flipping a test org's tier) without
    // forcing a page reload.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh()
    }
    document.addEventListener('visibilitychange', onVisibility)

    // Same-tab tier changes: useAuth dispatches this custom event after
    // /api/auth/me updates the agent, so the feature map stays in sync
    // without waiting for the interval.
    const onTierChanged = () => refresh()
    window.addEventListener('app:tier-may-have-changed', onTierChanged)

    // Safety-net polling — 60s is cheap and covers any case the events miss
    // (SSO flows, backend tier mutations that don't round-trip the client, etc).
    const interval = window.setInterval(refresh, 60_000)

    // Bridge from the api 402 interceptor: any locked-feature response pops
    // the upgrade modal with the right tier target.
    const unsubscribe = onFeatureLocked(({ feature, requiredTier }) => {
      setUpgradePrompt({ feature: feature as Feature, requiredTier: requiredTier as Tier })
    })

    return () => {
      window.removeEventListener('storage', onStorage)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('app:tier-may-have-changed', onTierChanged)
      window.clearInterval(interval)
      unsubscribe()
    }
  }, [refresh])

  const isAllowed = useCallback(
    (feature: Feature) => {
      if (state.isPlatformAdmin) return true
      return state.active.has(feature)
    },
    [state.isPlatformAdmin, state.active]
  )

  const requiredTier = useCallback(
    (feature: Feature) => state.locked.get(feature) ?? null,
    [state.locked]
  )

  const requestUpgrade = useCallback((feature: Feature, tier: Tier) => {
    setUpgradePrompt({ feature, requiredTier: tier })
  }, [])

  const closeUpgrade = useCallback(() => setUpgradePrompt(null), [])

  return (
    <FeatureGateContext.Provider
      value={{
        ...state,
        isAllowed,
        requiredTier,
        refresh,
        requestUpgrade,
        upgradePrompt,
        closeUpgrade,
      }}
    >
      {children}
    </FeatureGateContext.Provider>
  )
}

export function useFeatureGate() {
  return useContext(FeatureGateContext)
}
